import React, { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Checkbox } from "./ui/checkbox";
import { Plus, Minus, Utensils, X } from "lucide-react";
import { cn, formatCurrency } from "../lib/utils";

/**
 * Shared product customization modal — same engine for Customer, Waiter, Cashier.
 *
 * Props:
 *   item: menu item object (with variants[], extras[], image_url, image_data)
 *   open: boolean
 *   onClose: () => void
 *   onAdd: (cartLine) => void
 *   brandColor?: string (default sky)
 *
 * Output cart line shape (consistent for all callers):
 *   {
 *     id,                  // unique line id
 *     menu_item_id,
 *     name,
 *     price,               // unit price incl variant + extras
 *     quantity,
 *     variant_id,
 *     variant_name,
 *     selected_extras,     // [extra_id]
 *     extras_details,      // [{id, name, price}]
 *     notes,
 *     base_price,          // item base price (no extras)
 *     category_type        // food | drink (for kitchen routing display)
 *   }
 */
const ProductCustomizationModal = ({ item, open, onClose, onAdd, brandColor = "#0EA5E9" }) => {
  const [variant, setVariant] = useState(null);
  const [extras, setExtras] = useState([]);
  const [notes, setNotes] = useState("");
  const [quantity, setQuantity] = useState(1);

  // Reset whenever item changes
  useEffect(() => {
    if (item) {
      setVariant(item.variants?.[0] || null);
      setExtras([]);
      setNotes("");
      setQuantity(1);
    }
  }, [item]);

  const image = item?.image_data || item?.image_url || null;

  const extrasDetails = useMemo(() => {
    if (!item) return [];
    return extras
      .map(eid => item.extras?.find(e => e.id === eid))
      .filter(Boolean)
      .map(e => ({ id: e.id, name: e.name, price: e.price }));
  }, [extras, item]);

  const extrasTotal = extrasDetails.reduce((s, e) => s + (e.price || 0), 0);
  const variantMod = variant?.price_modifier || 0;
  const unitPrice = (item?.price || 0) + variantMod + extrasTotal;
  const total = unitPrice * quantity;

  const toggleExtra = (eid) => setExtras(prev => prev.includes(eid) ? prev.filter(x => x !== eid) : [...prev, eid]);

  const handleAdd = () => {
    if (!item) return;
    const line = {
      id: `${item.id}-${Date.now()}`,
      menu_item_id: item.id,
      name: item.name,
      price: unitPrice,
      base_price: item.price,
      quantity,
      variant_id: variant?.id || null,
      variant_name: variant?.name || null,
      selected_extras: extras,
      extras_details: extrasDetails,
      notes: notes.trim(),
      category_type: item.category_type || "food",
    };
    onAdd(line);
    onClose();
  };

  if (!item) return null;
  const isUnavailable = item.is_available === false;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md p-0 gap-0 overflow-hidden rounded-2xl sm:rounded-3xl max-h-[92vh] flex flex-col" data-testid="customize-modal">
        {/* Accessible title/description for screen readers */}
        <DialogTitle className="sr-only">Customize {item.name}</DialogTitle>
        <DialogDescription className="sr-only">Choose options, extras and quantity for {item.name}.</DialogDescription>

        {/* Close button overlay */}
        <button onClick={onClose} className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center shadow active:scale-90" aria-label="Close" data-testid="customize-close">
          <X className="w-4 h-4 text-slate-700" />
        </button>

        {/* Hero image */}
        {image ? (
          <img src={image} alt={item.name} className="w-full h-44 object-cover shrink-0" />
        ) : (
          <div className="w-full h-28 bg-slate-100 flex items-center justify-center shrink-0">
            <Utensils className="w-10 h-10 text-slate-300" />
          </div>
        )}

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 p-5 space-y-5">
          <div>
            <h2 className="text-xl font-bold text-slate-900">{item.name}</h2>
            {item.description && <p className="text-slate-500 text-sm mt-1">{item.description}</p>}
            <p className="text-2xl font-bold mt-2" style={{ color: brandColor }}>{formatCurrency(item.price)}</p>
            {isUnavailable && (
              <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 px-2 py-1 rounded-full">
                Currently unavailable
              </p>
            )}
          </div>

          {/* Variants — required if present */}
          {item.variants?.length > 0 && (
            <div>
              <Label className="text-slate-800 font-semibold text-sm mb-2 flex items-center justify-between">
                <span>Choose an option</span>
                <span className="text-xs text-slate-400 font-normal">Required</span>
              </Label>
              <RadioGroup value={variant?.id || ""} onValueChange={(v) => setVariant(item.variants.find(x => x.id === v))}>
                <div className="space-y-2">
                  {item.variants.map(v => (
                    <label key={v.id} htmlFor={`var-${v.id}`}
                      className={cn("flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all",
                        variant?.id === v.id ? "border-slate-900 bg-slate-50" : "border-slate-200 bg-white hover:border-slate-300"
                      )} data-testid={`variant-${v.id}`}>
                      <RadioGroupItem value={v.id} id={`var-${v.id}`} />
                      <span className="flex-1 text-slate-900 font-medium">{v.name}</span>
                      {v.price_modifier > 0 && <span className="text-sm text-slate-500">+{formatCurrency(v.price_modifier)}</span>}
                    </label>
                  ))}
                </div>
              </RadioGroup>
            </div>
          )}

          {/* Extras — optional */}
          {item.extras?.length > 0 && (
            <div>
              <Label className="text-slate-800 font-semibold text-sm mb-2 flex items-center justify-between">
                <span>Add extras</span>
                <span className="text-xs text-slate-400 font-normal">Optional</span>
              </Label>
              <div className="space-y-2">
                {item.extras.map(extra => (
                  <label key={extra.id}
                    className={cn("flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all",
                      extras.includes(extra.id) ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-white hover:border-slate-300"
                    )}
                    onClick={() => toggleExtra(extra.id)}
                    data-testid={`extra-${extra.id}`}>
                    <Checkbox checked={extras.includes(extra.id)} onCheckedChange={() => toggleExtra(extra.id)} />
                    <span className="flex-1 text-slate-900 font-medium">{extra.name}</span>
                    <span className="text-emerald-600 font-semibold text-sm">+{formatCurrency(extra.price)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <Label className="text-slate-800 font-semibold text-sm mb-2 block">Special instructions</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. No onions, extra spicy, no peanuts (allergic)"
              className="bg-white border-slate-200 rounded-xl resize-none" rows={2}
              data-testid="customize-notes" />
          </div>
        </div>

        {/* Footer: Quantity + Add */}
        <div className="border-t bg-white p-4 flex items-center gap-3 shrink-0">
          <div className="flex items-center gap-2 bg-slate-100 rounded-full px-2 py-1">
            <button className="w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center active:scale-90 disabled:opacity-40"
              onClick={() => setQuantity(q => Math.max(1, q - 1))}
              disabled={quantity <= 1}
              data-testid="qty-minus">
              <Minus className="w-4 h-4 text-slate-700" />
            </button>
            <span className="w-6 text-center font-bold text-base text-slate-900" data-testid="qty-value">{quantity}</span>
            <button className="w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center active:scale-90"
              onClick={() => setQuantity(q => q + 1)}
              data-testid="qty-plus">
              <Plus className="w-4 h-4 text-slate-700" />
            </button>
          </div>
          <Button className="flex-1 h-12 rounded-xl text-white text-base font-bold active:scale-[0.98]"
            style={{ backgroundColor: isUnavailable ? "#94a3b8" : brandColor }}
            onClick={handleAdd}
            disabled={isUnavailable}
            data-testid="customize-add-btn">
            {isUnavailable ? "Unavailable" : `Add · ${formatCurrency(total)}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProductCustomizationModal;
